import { messageAlert } from '../../utils';
import getAxios from '../../utils/baseApi';
import sha256 from 'js-sha256';
import { signIn, signOut, updateIsMasqueradeUser, updateIsRedirectedUser, updateOnlyDeveloperLicenses } from '../../redux/features/user/userslice';
import { updateLogin } from '../../components/search/redux/searchPeristReducer';
import { resetPaginationForAllObjectTypes } from '../../components/search/filters/utils';
import { updateUserPreferences } from '../../redux/features/settings/settingslice';
import { ADMIN_LICENSE, AI_EMAIL_LICENSE, CALENDAR_LICENSE, CONSTRUCTION_HUB_LICENSE, DEVELOPER_LICENSE, EXPORT_LICENSE, INSIDE_SALES_LICENSE, JUST_ASK_LICENSE, LANDMARK_LICENSE, WEB_APP_LICENSE } from '../../utils/settings';
import { fetchShareTeams, fetchUniverseCount, fetchEmailPrompts, fetchAllFrameworksList } from '../login/apiclient';
import { fetchCustomerProfile } from '../search/apiclient';
import { fetchBadgeCount, getNotifications } from '../header/apiclient';

/**
 * SSO Masquerade User Sign In
 * Called when user selects a user from the masquerade user list
 * Uses the new /auth/masquerade/select/user endpoint
 * Auth token is handled by Django session (stored during /auth/masquerade callback)
 */
export const signInMasqueradeUser = (username, sectorNo, masqueradedBy, navigate, dispatch, redirectTo = null) => {
    var axios = getAxios()
    
    let isAdminLicence = false
    let isInsideSalesLicence = false
    let isExportLicence = false
    let isConstructionHubLicence = false
    let isCalendarLicence = false
    let isJustAskLicence = false
    let isLandmarkLicence = false

    const payload = {
        username: username,
        sector_no: sectorNo,
        masqueraded_by: masqueradedBy
    }

    if (redirectTo != null && redirectTo.includes("useradmin#userActivity")) {
        payload['is_app_activity'] = 'True'
    } else {
        payload['is_app_activity'] = 'False'
    }

    axios.post('/auth/masquerade/select/user', payload)
    .then(async (response) => {
        // Set masquerade user flag
        dispatch(updateIsMasqueradeUser(true))
        dispatch(updateIsRedirectedUser(payload['is_app_activity'] === 'True' ? true : false))

        let userWithDeveloperLicenseOnly = false;

        // Check if any number in DEVELOPER_LICENSE is present in response.data.licence_ids
        const anyDeveloperInUserLicences = DEVELOPER_LICENSE.some(num => response.data.licence_ids.includes(num));

        // Check if no numbers in WEB_APP_LICENSE are present in response.data.licence_ids
        const noWebAppInUserLicences = WEB_APP_LICENSE.every(num => !response.data.licence_ids.includes(num));

        if (anyDeveloperInUserLicences && noWebAppInUserLicences) {
            userWithDeveloperLicenseOnly = true;
        }

        response.data.licence_ids.map(function (licence, index) {
            if (ADMIN_LICENSE.includes(licence)) {
                isAdminLicence = true
            }
            if (INSIDE_SALES_LICENSE.includes(licence)) {
                isInsideSalesLicence = true
            }
            if (EXPORT_LICENSE.includes(licence)) {
                isExportLicence = true
            }
            if (CONSTRUCTION_HUB_LICENSE.includes(licence)) {
                isConstructionHubLicence = true
            }
            if (CALENDAR_LICENSE.includes(licence)) {
                isCalendarLicence = true
            }
            if (JUST_ASK_LICENSE.includes(licence)) {
                isJustAskLicence = true
            }
            if (LANDMARK_LICENSE.includes(licence)) {
                isLandmarkLicence = true
            }
        })

        const onlyHasJustAskLicense = (response.data.licence_ids.length === 1 && response.data.licence_ids[0] === 1084)
            || response.data.licence_ids.length === 2 && [1072, 1084].every((value, index) => value === response.data.licence_ids[index]);

        response.data['licenceFlag'] = {}
        response.data['licenceFlag']['isAdminLicence'] = isAdminLicence
        response.data['licenceFlag']['isInsideSalesLicence'] = isInsideSalesLicence
        response.data['licenceFlag']['isExportLicence'] = isExportLicence
        response.data['licenceFlag']['isConstructionHubLicence'] = isConstructionHubLicence
        response.data['licenceFlag']['isCalendarLicence'] = isCalendarLicence
        response.data['licenceFlag']['isJustAskLicence'] = isJustAskLicence
        response.data['licenceFlag']['isLandmarkLicence'] = isLandmarkLicence
        response.data['licenceFlag']['onlyHasJustAskLicense'] = onlyHasJustAskLicense
        response.data['licenceFlag']['isAIEmailLicence'] = AI_EMAIL_LICENSE.some(num => response.data.licence_ids.includes(num))

        dispatch(signIn(response.data))
        dispatch(updateLogin(response.data.default_saved_search ? true : false))
        resetPaginationForAllObjectTypes(dispatch)
        fetchUniverseCount(dispatch)
        dispatch(updateOnlyDeveloperLicenses(userWithDeveloperLicenseOnly))
        
        await axios.get(`/users/${response.data.user_id}`).then((response) => {
            if (response.status === 200) {
                dispatch(updateUserPreferences(response.data.user.preferences))
            }
        })
        
        fetchEmailPrompts(dispatch, response.data.user_id)
        fetchShareTeams(dispatch, response.data)
        getNotifications(response.data.user_id, dispatch);
        fetchBadgeCount(response.data.user_id, response.data.licenceFlag, dispatch);
        fetchAllFrameworksList(dispatch)
        fetchCustomerProfile(dispatch)
        
        // Navigate to appropriate page (skip Intercom for masquerade users)
        if (redirectTo) {
            navigate(redirectTo)
        } else {
            navigate(userWithDeveloperLicenseOnly ? "/app/settings#notifications" : onlyHasJustAskLicense ? "/app/justask" : "/app/list")
        }
    })
    .catch(function (error) {
        if (error.response) {
            let internalServerErrorMessage = "Oops, something has gone wrong# Please try again"
            let serviceUnavailableError = "Server is not responding. Please try again later"
            if (error.response.status === 500) {
                messageAlert(dispatch, internalServerErrorMessage, 'danger')
            }
            else if (error.response.status === 503) {
                messageAlert(dispatch, serviceUnavailableError, 'danger')
            }
            else if (error.response.status === 403) {
                messageAlert(dispatch, internalServerErrorMessage, 'danger')
            }
            else if (error.response.status === 401) {
                messageAlert(dispatch, error.response.data?.error || error.response.data, 'danger')
            }
            else {
                messageAlert(dispatch, error.response, 'danger')
            }
        }
    });
}

export const masqueradeUser = (token, salesforce = false, context, dispatch, navigate) => {
    var axios = getAxios()
    if (!token) {
        // No token means the page was loaded without a masquerade URL — bail out.
        messageAlert(dispatch, 'Missing masquerade token. Please use the original masquerade link.', 'danger')
        navigate('/')
        return
    }
    if (token.indexOf(' ') != -1) {
        token  = token.replaceAll(' ', '+')
    }
    let url = '/auth/masquerade?token=' + token
    if (salesforce === "true") {
        url += '&salesforce=true'
    }
    axios.get(url)
            .then(function (response) {
                
                let responseJson = {
                    user: response.data.data
                }
                responseJson['user']['is_cars_user'] = true
                context.dispatchMasqueradeEvent('MASQUERADE_USER_LIST', responseJson)

                // Auto-redirect flow: sign in directly using web_username
                if ('redirect_to' in response.data.data && 'sector_no' in response.data.data && 'web_username' in response.data.data && response.data.data.web_username) {
                    signInMasqueradeUser(
                        response.data.data.web_username,
                        response.data.data.sector_no,
                        response.data.data.cars_user_no,
                        navigate,
                        dispatch,
                        response.data.data.redirect_to
                    )
                    context.dispatchMasqueradeEvent('REDIRECT_USER', true)
                } else {
                    context.dispatchMasqueradeEvent('REDIRECT_USER', false)
                }
                context.dispatchMasqueradeEvent('MASQUERADE_USER', false)
                return response
            })
            .catch(function (error) {
                if (!error.response) {
                    // No HTTP response — covers all cases where the browser followed
                    // a cross-origin OAuth redirect chain (CORS error, network error,
                    // TypeError: Failed to fetch, AxiosError, etc.).
                    // Navigate the full browser window so Django can redirect to
                    // the backend for SSO login.
                    window.location.href = url
                    return
                }
                if (error.response.status === 401) {
                    messageAlert(dispatch, "Reusing the URL is forbidden, please click on Masquerade again.", 'danger')
                    setTimeout(() => navigate('/'), 3000)
                }
            })
}

/**
 * Stop masquerade — called from the "Stop masquerade" button in the header.
 *
 * Flow:
 *  1. Axios GET /auth/masquerade/logout → Django clears its session and
 *     returns { success: true, redirect: "<flask_logout_url>" }
 *  2. Redux store is cleared immediately so local state is clean.
 *  3. Browser navigates to the Flask logout URL (window.location.href) so
 *     Flask can delete Redis keys, clear its own session cookie, and then
 *     redirect the browser to Azure AD logout.
 *  4. Azure AD clears its SSO session and redirects the browser back to
 *     the application (WEB_BASE_URL), forcing a fresh SSO login next time.
 */
export const stopMasqueradeUser = (dispatch, navigate) => {
    const axios = getAxios()
    axios.get('/auth/masquerade/logout')
        .then((response) => {
            dispatch(signOut())
            if (response.data && response.data.redirect) {
                // Cross-origin: send the browser to Flask → Azure AD logout.
                // After Azure AD clears its SSO session it redirects back to
                // the app (WEB_BASE_URL), landing the user at '/'.
                window.location.href = response.data.redirect
            } else {
                // No SSO redirect needed — use React Router to stay in-app.
                navigate('/')
            }
        })
        .catch(() => {
            // Even on error, clear local state so the user isn't stuck
            dispatch(signOut())
            navigate('/')
        })
}